/**
 * Party Registration Status — Clause 44 analysis screen (2026-02-28).
 *
 * Lets the auditor review, per party, the GST registration status and
 * GSTIN the engine actually used to classify that party's vouchers —
 * and where it came from — then correct it directly here.  A saved
 * correction is scoped to the client, re-classifies this run
 * immediately, and also carries forward to future periods/re-uploads
 * for the same client.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { MagnifyingGlass, PencilSimple, ArrowCounterClockwise, ShieldCheck, Bank } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { formatINR } from "@/lib/format";
import { getPartyRegistration, setPartyRegistrationOverride, clearPartyRegistrationOverride } from "@/lib/api";

const STATUS_BADGE = {
  registered: { label: "Registered", cls: "bg-emerald-50 text-emerald-800 border-emerald-300" },
  unregistered: { label: "Unregistered", cls: "bg-rose-50 text-rose-800 border-rose-300" },
  mixed: { label: "Mixed", cls: "bg-amber-50 text-amber-900 border-amber-300" },
};

const FILTERS = [
  { key: "all", label: "All" },
  { key: "unregistered", label: "Unregistered" },
  { key: "mixed", label: "Mixed" },
  { key: "has_override", label: "Corrected" },
];

export default function PartyRegistrationPanel({ run, onClassificationChange }) {
  const runId = run?.run_id;
  const [loading, setLoading] = useState(true);
  const [parties, setParties] = useState([]);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null); // party row being edited

  const load = async () => {
    setLoading(true);
    try {
      const r = await getPartyRegistration(runId);
      setParties(r.parties || []);
    } catch (e) {
      toast.error("Failed to load party registration data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (runId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  const counts = useMemo(() => {
    const c = { all: parties.length, unregistered: 0, mixed: 0, has_override: 0 };
    parties.forEach((p) => {
      if (p.registration_status === "unregistered") c.unregistered += 1;
      if (p.registration_status === "mixed") c.mixed += 1;
      if (p.has_override) c.has_override += 1;
    });
    return c;
  }, [parties]);

  const visible = useMemo(() => {
    let rows = parties;
    if (filter === "unregistered") rows = rows.filter((p) => p.registration_status === "unregistered");
    else if (filter === "mixed") rows = rows.filter((p) => p.registration_status === "mixed");
    else if (filter === "has_override") rows = rows.filter((p) => p.has_override);
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      rows = rows.filter((p) => p.party_name.toLowerCase().includes(q) || (p.gstin || "").toLowerCase().includes(q));
    }
    return rows;
  }, [parties, filter, query]);

  const applyResult = (resp) => {
    // Refresh this party's row locally + bubble the fresh summary/by_party
    // up to StepReport so the Schedule tab reflects the correction too,
    // without a full page reload.
    load();
    if (onClassificationChange) onClassificationChange(resp);
  };

  const handleSave = async (payload) => {
    try {
      const resp = await setPartyRegistrationOverride(runId, payload);
      toast.success(`Registration status saved for “${payload.party_name}”`);
      setEditing(null);
      applyResult(resp);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save override");
    }
  };

  const handleClear = async (partyName) => {
    try {
      const resp = await clearPartyRegistrationOverride(runId, partyName);
      toast.success(`Override cleared for “${partyName}” — reverted to auto-detected`);
      applyResult(resp);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to clear override");
    }
  };

  if (loading) {
    return <div className="py-10 text-center font-mono text-sm text-[#8A8A83]" data-testid="party-registration-loading">Loading party registration data…</div>;
  }

  return (
    <div data-testid="party-registration-panel">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[240px] max-w-sm">
          <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8A8A83]"/>
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search party name or GSTIN…"
            className="pl-8 h-9 text-sm rounded-sm"
            data-testid="party-registration-search"
          />
        </div>
        <div className="flex items-center gap-1.5">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`party-registration-filter-${f.key}`}
              className={`h-8 px-3 rounded-sm border font-mono text-[10.5px] uppercase tracking-[0.1em] ${
                filter === f.key
                  ? "bg-[#0F172A] text-white border-[#0F172A]"
                  : "bg-white text-[#52524E] border-[#E5E5E0] hover:bg-[#F3F4F1]"
              }`}
            >
              {f.label} <span className="opacity-70">({counts[f.key] ?? 0})</span>
            </button>
          ))}
        </div>
      </div>

      <div className="border border-[#E5E5E0] rounded-sm bg-white overflow-x-auto" data-testid="party-registration-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F3F4F1] border-b border-[#E5E5E0] text-left font-mono text-[10px] uppercase tracking-[0.1em] text-[#52524E]">
              <th className="px-3 py-2.5">Party Name</th>
              <th className="px-3 py-2.5">GSTIN</th>
              <th className="px-3 py-2.5">Status</th>
              <th className="px-3 py-2.5">GST Scheme</th>
              <th className="px-3 py-2.5">Source of GSTIN</th>
              <th className="px-3 py-2.5 text-right">Amount</th>
              <th className="px-3 py-2.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-[#8A8A83] font-mono text-xs" data-testid="party-registration-empty">No parties match this filter.</td></tr>
            )}
            {visible.map((p) => {
              const badge = STATUS_BADGE[p.registration_status] || STATUS_BADGE.unregistered;
              return (
                <tr key={p.party_name} className="border-b border-[#F0F0EC] hover:bg-[#FAFAF7]" data-testid={`party-row-${p.party_name}`}>
                  <td className="px-3 py-2.5 max-w-[280px]">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate" title={p.party_name}>{p.party_name || "— Cash / No Party —"}</span>
                      {p.is_bank_party && <Bank size={12} className="text-[#8A8A83] shrink-0" title="Auto-detected bank account"/>}
                      {p.has_override && <ShieldCheck size={12} className="text-emerald-600 shrink-0" title="Manually verified/overridden"/>}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 font-mono text-[12px]" data-testid={`party-gstin-${p.party_name}`}>{p.gstin || "—"}</td>
                  <td className="px-3 py-2.5">
                    <Badge className={`rounded-sm shadow-none border font-mono text-[10px] uppercase tracking-[0.08em] ${badge.cls}`} data-testid={`party-status-${p.party_name}`}>
                      {badge.label}
                    </Badge>
                  </td>
                  <td className="px-3 py-2.5 text-[#52524E]">{p.gst_scheme}</td>
                  <td className="px-3 py-2.5 text-[#8A8A83] text-[12.5px]">{p.source_of_gstin}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-[12.5px]">{formatINR(p.total_amount)}</td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    <button
                      onClick={() => setEditing(p)}
                      className="inline-flex items-center gap-1 h-7 px-2 border border-[#D4D4D0] rounded-sm hover:bg-[#F3F4F1] font-mono text-[10px] uppercase tracking-[0.08em]"
                      data-testid={`party-verify-btn-${p.party_name}`}
                    >
                      <PencilSimple size={11}/> Verify
                    </button>
                    {p.has_override && (
                      <button
                        onClick={() => handleClear(p.party_name)}
                        className="ml-1.5 inline-flex items-center gap-1 h-7 px-2 border border-[#D4D4D0] rounded-sm hover:bg-[#F3F4F1] font-mono text-[10px] uppercase tracking-[0.08em] text-[#52524E]"
                        data-testid={`party-reset-btn-${p.party_name}`}
                        title="Revert to auto-detected registration status"
                      >
                        <ArrowCounterClockwise size={11}/> Reset
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && (
        <PartyOverrideDialog
          party={editing}
          onClose={() => setEditing(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

const VALID_SCHEMES = ["regular", "composition", "other"];

function PartyOverrideDialog({ party, onClose, onSave }) {
  const [gstin, setGstin] = useState(party.override?.gstin || (party.gstin !== "—" ? party.gstin : "") || "");
  const [status, setStatus] = useState(party.override?.registration_status || party.registration_status || "registered");
  const initialScheme = party.override?.gst_scheme || party.gst_scheme_raw || "regular";
  const [scheme, setScheme] = useState(VALID_SCHEMES.includes(initialScheme) ? initialScheme : "regular");
  const [note, setNote] = useState(party.override?.note || "");
  const [saving, setSaving] = useState(false);

  const handleStatusChange = (v) => {
    setStatus(v);
    // Auto-detected "unregistered"/"mixed"/"" schemes are not valid once
    // the auditor flips status to Registered — default to Regular so the
    // saved payload is never contradictory (registered + scheme=unregistered).
    if (v === "registered" && !VALID_SCHEMES.includes(scheme)) {
      setScheme("regular");
    }
  };

  const submit = async () => {
    setSaving(true);
    try {
      await onSave({
        party_name: party.party_name,
        gstin: status === "registered" ? gstin.trim() : "",
        registration_status: status,
        gst_scheme: status === "registered" ? scheme : null,
        note: note.trim() || null,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[460px] rounded-sm" data-testid="party-override-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">Verify Registration Status</DialogTitle>
          <DialogDescription className="text-[13px]">
            <strong>{party.party_name}</strong> — correcting this flows through to Clause 44 classification immediately for this run, and applies to future periods for this client too.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div>
            <label className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-[#52524E]">Registration Status</label>
            <Select value={status} onValueChange={handleStatusChange}>
              <SelectTrigger className="mt-1.5 h-9 rounded-sm" data-testid="override-status-select">
                <SelectValue/>
              </SelectTrigger>
              <SelectContent>

                <SelectItem value="registered">Registered</SelectItem>
                <SelectItem value="unregistered">Unregistered</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {status === "registered" && (
            <>
              <div>
                <label className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-[#52524E]">GSTIN</label>
                <Input
                  value={gstin}
                  onChange={(e) => setGstin(e.target.value.toUpperCase())}
                  placeholder="e.g. 33AFQPJ1225D1ZW"
                  className="mt-1.5 h-9 rounded-sm font-mono text-sm"
                  data-testid="override-gstin-input"
                  maxLength={15}
                />
              </div>
              <div>
                <label className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-[#52524E]">GST Scheme</label>
                <Select value={scheme} onValueChange={setScheme}>
                  <SelectTrigger className="mt-1.5 h-9 rounded-sm" data-testid="override-scheme-select">
                    <SelectValue/>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="regular">Regular</SelectItem>
                    <SelectItem value="composition">Composition</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          <div>
            <label className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-[#52524E]">Note (optional)</label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Verified GSTIN on GST portal, 12-Feb-2026"
              className="mt-1.5 rounded-sm text-sm"
              rows={2}
              data-testid="override-note-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="font-mono text-[11px] uppercase tracking-[0.1em]" data-testid="override-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving || (status === "registered" && !gstin.trim())}
            className="bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-sm shadow-none font-mono text-[11px] uppercase tracking-[0.1em]"
            data-testid="override-save-btn"
          >
            {saving ? "Saving…" : "Save & Reclassify"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
